import React from "react";
import { Container, Alert, Box, Skeleton, useTheme } from "@mui/material";
import { useCompanyQuote, Quote } from "../Charts/hooks/useCompanyQuote";
import { useCompanyProfile, CompanyProfile } from "../Charts/hooks/useCompanyProfile";
import ProjectionsWrapper from "./ProjectionsWrapper";
import ErrorDisplay from "../../common/ErrorDisplay";
import { StockCurrencyProvider } from "../../../contexts/StockCurrencyContext";

/**
 * Simple loading skeleton for the projections page
 */
const ProjectionsSkeleton: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const flatUIColors = {
    background: isDark ? "#1a1a1a" : "#ffffff",
    surface: isDark ? "#212121" : "#f5f5f5",
    border: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: flatUIColors.background,
      }}
    >
      {/* Header Skeleton */}
      <Box
        sx={{
          height: 64,
          display: "flex",
          alignItems: "center",
          px: 3,
          borderBottom: `1px solid ${flatUIColors.border}`,
          gap: 2,
        }}
      >
        <Skeleton
          variant="circular"
          width={40}
          height={40}
          sx={{ backgroundColor: flatUIColors.border }}
        />
        <Box sx={{ flex: 1 }}>
          <Skeleton
            variant="text"
            width={150}
            height={24}
            sx={{ backgroundColor: flatUIColors.border }}
          />
          <Skeleton
            variant="text"
            width={80}
            height={16}
            sx={{ backgroundColor: flatUIColors.border }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 3 }}>
          <Box>
            <Skeleton
              variant="text"
              width={60}
              height={14}
              sx={{ backgroundColor: flatUIColors.border }}
            />
            <Skeleton
              variant="text"
              width={80}
              height={28}
              sx={{ backgroundColor: flatUIColors.border }}
            />
          </Box>
          <Box>
            <Skeleton
              variant="text"
              width={80}
              height={14}
              sx={{ backgroundColor: flatUIColors.border }}
            />
            <Skeleton
              variant="text"
              width={80}
              height={28}
              sx={{ backgroundColor: flatUIColors.border }}
            />
          </Box>
        </Box>
      </Box>

      {/* Tabs Skeleton */}
      <Box sx={{ px: 3, pt: 2, display: "flex", gap: 1 }}>
        <Skeleton
          variant="rectangular"
          width={140}
          height={32}
          sx={{ backgroundColor: flatUIColors.border, borderRadius: 0.5 }}
        />
        <Skeleton
          variant="rectangular"
          width={120}
          height={32}
          sx={{ backgroundColor: flatUIColors.border, borderRadius: 0.5 }}
        />
      </Box>

      {/* Content Skeleton */}
      <Box sx={{ p: 3, display: "flex", gap: 3 }}>
        {/* Sidebar */}
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={400}
            sx={{ backgroundColor: flatUIColors.surface, borderRadius: 2 }}
          />
        </Box>
        {/* Main Content */}
        <Box sx={{ flex: 1 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={500}
            sx={{ backgroundColor: flatUIColors.surface, borderRadius: 2 }}
          />
        </Box>
      </Box>
    </Box>
  );
};

interface ProjectionsLoaderProps {
  ticker: string;
}

/**
 * Loader component that handles data fetching, loading states, and error handling
 * for the Projections page. Wraps ProjectionsWrapper with StockCurrencyProvider.
 */
const ProjectionsLoader: React.FC<ProjectionsLoaderProps> = ({ ticker }) => {
  // Fetch quote data
  const {
    data: quoteData,
    error: quoteError,
    isLoading: quoteLoading,
  } = useCompanyQuote(ticker);

  // Fetch profile data for currency context
  const {
    data: profileData,
    error: profileError,
    isLoading: profileLoading,
  } = useCompanyProfile(ticker);

  // Show skeleton while loading
  if (quoteLoading || profileLoading) {
    return <ProjectionsSkeleton />;
  }

  // Show error state
  if (quoteError || profileError) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <ErrorDisplay
          error={quoteError ?? profileError}
          onRetry={() => window.location.reload()}
          variant="card"
          fullWidth
        />
      </Container>
    );
  }

  // Extract quote (handle array or single object response)
  let quote: Quote | undefined;
  if (Array.isArray(quoteData)) {
    quote = quoteData[0];
  } else if (quoteData && typeof quoteData === "object") {
    if ((quoteData as any).data) {
      quote = (quoteData as any).data;
    } else {
      quote = quoteData;
    }
  }

  // Extract profile
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;

  // Validate we have required data
  if (!quote || !quote.symbol) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="info">No company data found for ticker: {ticker}</Alert>
      </Container>
    );
  }

  // Render ProjectionsWrapper with currency context
  return (
    <StockCurrencyProvider
      currency={profile?.currency}
      exchange={profile?.exchange}
    >
      <ProjectionsWrapper
        ticker={ticker}
        quote={quote}
        profile={profile}
      />
    </StockCurrencyProvider>
  );
};

export default ProjectionsLoader;
